import type { FormationStatusState } from '@/types/formationStatus';

type Translate = (key: string) => string;

export interface FormationStartBody {
  platform_id: number;
  date_from: string;
  date_to: string;
  client_ids: number[];
  suppliers: { supplier_id: number; operations: Record<string, unknown>[] }[];
}

/** Fields of a screen row that work-calc compares with the mart. */
const SNAPSHOT_OPERATION_FIELDS = ['id', 'actual_size', 'actual_work_time', 'quality', 'performance', 'rate', 'total'];

/** The starter gave no answer of its own: the gateway or the route stopped waiting. */
const NO_RESPONSE_STATUSES = [502, 503, 504];

export function platformIdOfOperation(operation: any): string {
  const id = operation?.application?.platform?.id ?? operation?.application?.Platform?.id;
  return id !== undefined && id !== null ? String(id) : '';
}

/** Two decimals: the precision the formation table shows and saves with. */
export function decimal2(value: unknown): string {
  const n = Number(String(value).replace(',', '.'));
  return Number.isNaN(n) ? '0.00' : n.toFixed(2);
}

/**
 * Screen snapshot of one platform card. Operations go as loaded — no rounding, no recount, `null` stays `null`:
 * work-calc compares them with the mart. Suppliers keep the card order, first appearance in the operations.
 * `client_ids` is the client filter, not the clients of the shown operations: D looks by it for confirmed
 * applications left outside the snapshot.
 */
export function buildFormationStartBody(
  platformId: string,
  dates: string[],
  clientIds: (string | number)[],
  operations: any[],
): FormationStartBody {
  const suppliers = new Map<string, FormationStartBody['suppliers'][number]>();
  operations
    .filter((operation) => platformIdOfOperation(operation) === String(platformId))
    .forEach((operation) => {
      const supplierId = String(operation.application.Supplier.id);
      const supplier = suppliers.get(supplierId) ?? { supplier_id: Number(supplierId), operations: [] };
      supplier.operations.push(SNAPSHOT_OPERATION_FIELDS.reduce<Record<string, unknown>>(
        (row, field) => ({ ...row, [field]: operation[field] ?? null }),
        {},
      ));
      suppliers.set(supplierId, supplier);
    });
  return {
    platform_id: Number(platformId),
    date_from: dates[0],
    date_to: dates[1],
    client_ids: clientIds.map(Number),
    suppliers: Array.from(suppliers.values()),
  };
}

/**
 * An edit the user has not saved: the row's volume or mean differs from the baseline that loading and saving set.
 * Not the per-operation comparison `saveQualityPerformance()` picks operations by: an untouched row whose operations
 * differ in quality would count as edited, and saving it would overwrite them with the mean.
 */
export function isFormationRowEdited(row: any): boolean {
  return decimal2(row.sumActualSize) !== decimal2(row.oldSumActualSize)
    || decimal2(row.meanQuality) !== decimal2(row.baselineMeanQuality)
    || decimal2(row.meanPerformance) !== decimal2(row.baselineMeanPerformance);
}

/** Clients whose table holds an unsaved edit on the platform; a row knows its platform only through its operations. */
export function clientsWithUnsavedRows(
  groups: Record<string, any[]> | null,
  operations: any[],
  platformId: string,
): string[] {
  if (!groups) {
    return [];
  }
  const platformByOperationId = new Map(
    operations.map((operation) => [String(operation.id), platformIdOfOperation(operation)]),
  );
  return Object.keys(groups).filter((client) => (groups[client] ?? []).some((row) => isFormationRowEdited(row)
    && (row.ids ?? []).some((id: unknown) => platformByOperationId.get(String(id)) === String(platformId))));
}

function supplierState(supplier: any, t: Translate): FormationStatusState {
  const base = { supplierId: Number(supplier?.supplierId), legalName: String(supplier?.legalName ?? '') };
  const error = typeof supplier?.error === 'string' ? supplier.error : '';
  if (supplier?.status === 'started' && supplier?.jbpmProcessId) {
    return {
      ...base, status: 'pending', jbpmProcessId: String(supplier.jbpmProcessId), pollNetworkRetry: false,
    };
  }
  if (supplier?.status === 'failed') {
    return { ...base, status: 'failed', user_hint: error || t('formation_sheet_error_post_internal_error') };
  }
  // `unknown` and anything the contract does not name: the process may be running, there is nothing to poll.
  return { ...base, status: 'unknown', user_hint: error || t('formation_sheet_start_no_response') };
}

/**
 * Starter answer → formation alerts of the platform card. `status` and `data` come from the response, or from the
 * axios error when the request failed; `errorMessage` is the axios text for a request that got no response at all.
 */
export function formationStatesFromStartResponse(
  status: number | undefined,
  data: unknown,
  errorMessage: string,
  t: Translate,
): FormationStatusState[] {
  const body = data && typeof data === 'object' ? data as Record<string, any> : null;
  // 200, or 502 `jbpm_start_failed` when no process started: an outcome per supplier either way.
  if (body && Array.isArray(body.suppliers)) {
    return body.suppliers.map((supplier: unknown) => supplierState(supplier, t));
  }

  let errorTrace = errorMessage;
  if (typeof data === 'string' && data) {
    errorTrace = data;
  } else if (body) {
    errorTrace = JSON.stringify(body, null, 2);
  }
  if (body && typeof body.errorCode === 'string') {
    return [{
      status: 'failed',
      error_code: body.errorCode,
      user_hint: typeof body.error === 'string' ? body.error : t('formation_sheet_error_post_internal_error'),
      error_trace: errorTrace,
    }];
  }

  if (status === 401) {
    return [{
      status: 'failed', error_code: 'POST_AUTH_DENIED', user_hint: t('formation_sheet_error_post_auth_denied'), error_trace: errorTrace,
    }];
  }
  // Processes may have started: the gateway's 500 with a `ReadTimeout` cause is its own 540 s wait running out.
  if (status === undefined
    || NO_RESPONSE_STATUSES.includes(status)
    || (status === 500 && String(body?.error_cause ?? '').startsWith('ReadTimeout'))) {
    return [{
      status: 'failed', error_code: 'START_NO_RESPONSE', user_hint: t('formation_sheet_start_no_response'), error_trace: errorTrace,
    }];
  }
  return [{
    status: 'failed', error_code: 'POST_INTERNAL_ERROR', user_hint: t('formation_sheet_error_post_internal_error'), error_trace: errorTrace,
  }];
}
